import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";

// Contact role types - must match backend definition
export type ContactRole =
  | "workday-admin"
  | "sso-admin"
  | "it-admin"
  | "ta-admin";

// Serializable user data (Firebase User objects can't be JSON serialized)
export interface SerializableUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
}

interface UserState {
  user: SerializableUser | null;
  project: string;
  roles: ContactRole[]; // User's assigned roles from Firebase custom claims
  rolesLoaded: boolean; // Flag to indicate if roles have been loaded from Firebase
  setUser: (user: SerializableUser | null) => void;
  setProject: (user: string) => void;
  setLoading: (loading: boolean) => void;
  setRoles: (roles: ContactRole[]) => void;
  removeUser: () => void;
  loading: boolean;
  // Helper methods for role checking
  hasRole: (role: ContactRole) => boolean;
  hasAnyRole: (roles: ContactRole[]) => boolean;
  canAccessWorkday: () => boolean;
  canAccessDomain: () => boolean;
  canAccessSSO: () => boolean;
}

export const useUserStore = create<UserState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        loading: true,
        project: "rooster-impl",
        roles: [],
        rolesLoaded: false,
        setUser: (user) => set({ user }),
        setProject: (project: string) => set({ project }),
        setRoles: (roles: ContactRole[]) => set({ roles, rolesLoaded: true }),
        removeUser: () => set({ user: null, roles: [], rolesLoaded: false }),
        setLoading: (loading: boolean) => set({ loading }),
        // Helper methods
        hasRole: (role: ContactRole) => get().roles.includes(role),
        hasAnyRole: (roles: ContactRole[]) =>
          roles.some((role) => get().roles.includes(role)),
        canAccessWorkday: () => get().roles.includes("workday-admin"),
        canAccessDomain: () => get().roles.includes("it-admin"),
        canAccessSSO: () => get().roles.includes("sso-admin")
      }),
      {
        name: "user-storage",
        storage: createJSONStorage(() => sessionStorage)
      }
    )
  )
);

// Types for instructions page data
export type WorkdayRegion = "US" | "EU" | "";
export type ZoomConnected = "Yes" | "No" | "";
export type TargetEnvironment =
  | "production"
  | "implementation"
  | "production-europe"
  | "implementation-europe"
  | "";

interface InstructionsData {
  workdayRegion: WorkdayRegion;
  zoomConnected: ZoomConnected;
  targetEnvironment: TargetEnvironment;
}

// Stage completion tracking for sequenced workflow
// Note: completedAt is stored as ISO string for proper JSON serialization in localStorage
export interface StageCompletion {
  completedAt: string | null;
  completedBy: string | null;
}

interface StageCompletionState {
  workday: StageCompletion;
  domain: StageCompletion;
  sso: StageCompletion;
}

// Navigation context from backend-governed onboarding
export interface NavigationContext {
  accessiblePages: {
    path: string;
    label: string;
    order: number;
    stage: string;
  }[];
  startingPage: string;
  allStagesComplete: boolean;
  userRoles: string[];
  roosterUrl: string | null;
  canProceedFrom: { workday: boolean; domain: boolean; sso: boolean };
  waitingMessage: string | null;
  targetEnvironment: string;
  workdayRegion: string;
}

interface OnboardingState {
  // Existing fields
  uuid: string | null;
  tenantId: string | null;

  // Instructions page data
  instructionsData: InstructionsData;

  // Stage completion tracking
  stageCompletion: StageCompletionState;

  // Navigation context from backend
  navigationContext: NavigationContext | null;

  // Existing actions
  setUuid: (uuid: string) => void;
  setTenantId: (tenantId: string) => void;
  clearOnboarding: () => void;

  // Instructions data actions (populated from Firebase custom claims)
  setInstructionsData: (data: Partial<InstructionsData>) => void;
  setInstructionsDataFromClaims: (claims: Record<string, unknown>) => void;

  // Stage completion actions
  setStageCompletion: (
    stage: keyof StageCompletionState,
    completion: StageCompletion
  ) => void;
  markStageComplete: (
    stage: keyof StageCompletionState,
    completedBy: string
  ) => void;
  isStageComplete: (stage: keyof StageCompletionState) => boolean;
  loadStageCompletionFromRequest: (
    request: {
      workdayCompletion?: {
        completedAt?: unknown;
        completedBy?: string | null;
      } | null;
      domainCompletion?: {
        completedAt?: unknown;
        completedBy?: string | null;
      } | null;
      ssoCompletion?: {
        completedAt?: unknown;
        completedBy?: string | null;
      } | null;
    } | null
  ) => void;

  // Navigation context actions
  setNavigationContext: (ctx: NavigationContext | null) => void;
}

const initialInstructionsData: InstructionsData = {
  workdayRegion: "",
  zoomConnected: "",
  targetEnvironment: ""
};

const initialStageCompletion: StageCompletionState = {
  workday: { completedAt: null, completedBy: null },
  domain: { completedAt: null, completedBy: null },
  sso: { completedAt: null, completedBy: null }
};

export const useOnboardingStore = create<OnboardingState>()(
  devtools(
    persist(
      (set, get) => ({
        // Existing state
        uuid: null,
        tenantId: null,

        // Instructions page state
        instructionsData: initialInstructionsData,

        // Stage completion state
        stageCompletion: initialStageCompletion,

        // Navigation context state
        navigationContext: null,

        // Existing actions
        setUuid: (uuid: string) => set({ uuid }),
        setTenantId: (tenantId: string) => set({ tenantId }),
        clearOnboarding: () =>
          set({
            uuid: null,
            tenantId: null,
            instructionsData: initialInstructionsData,
            stageCompletion: initialStageCompletion,
            navigationContext: null
          }),

        // Instructions data actions (populated from Firebase custom claims)
        setInstructionsData: (data: Partial<InstructionsData>) =>
          set((state) => ({
            instructionsData: { ...state.instructionsData, ...data }
          })),

        setInstructionsDataFromClaims: (claims: Record<string, unknown>) => {
          const workdayRegion = (claims.workdayRegion as WorkdayRegion) || "";
          const zoomConnected = (claims.zoomConnected as ZoomConnected) || "";
          const targetEnvironment =
            (claims.targetEnvironment as TargetEnvironment) || "";

          set({
            instructionsData: {
              workdayRegion,
              zoomConnected,
              targetEnvironment
            }
          });
        },

        // Stage completion actions
        setStageCompletion: (
          stage: keyof StageCompletionState,
          completion: StageCompletion
        ) =>
          set((state) => ({
            stageCompletion: { ...state.stageCompletion, [stage]: completion }
          })),

        markStageComplete: (
          stage: keyof StageCompletionState,
          completedBy: string
        ) =>
          set((state) => ({
            stageCompletion: {
              ...state.stageCompletion,
              [stage]: { completedAt: new Date().toISOString(), completedBy }
            }
          })),

        isStageComplete: (stage: keyof StageCompletionState) => {
          const { stageCompletion } = get();
          return stageCompletion[stage].completedAt !== null;
        },

        // Navigation context actions
        setNavigationContext: (ctx: NavigationContext | null) =>
          set({ navigationContext: ctx }),

        loadStageCompletionFromRequest: (
          request: {
            workdayCompletion?: {
              completedAt?: unknown;
              completedBy?: string | null;
            } | null;
            domainCompletion?: {
              completedAt?: unknown;
              completedBy?: string | null;
            } | null;
            ssoCompletion?: {
              completedAt?: unknown;
              completedBy?: string | null;
            } | null;
          } | null
        ) => {
          if (!request) return;

          // Helper to normalize date to ISO string (handles both Date objects and strings from API)
          const toISOString = (date: unknown): string =>
            date instanceof Date ? date.toISOString() : String(date);

          const newStageCompletion: StageCompletionState = {
            workday: request.workdayCompletion
              ? {
                  completedAt: toISOString(
                    request.workdayCompletion.completedAt
                  ),
                  completedBy: request.workdayCompletion.completedBy ?? null
                }
              : { completedAt: null, completedBy: null },
            domain: request.domainCompletion
              ? {
                  completedAt: toISOString(
                    request.domainCompletion.completedAt
                  ),
                  completedBy: request.domainCompletion.completedBy ?? null
                }
              : { completedAt: null, completedBy: null },
            sso: request.ssoCompletion
              ? {
                  completedAt: toISOString(request.ssoCompletion.completedAt),
                  completedBy: request.ssoCompletion.completedBy ?? null
                }
              : { completedAt: null, completedBy: null }
          };

          set({ stageCompletion: newStageCompletion });
        }
      }),
      {
        name: "onboarding-storage",
        // Use sessionStorage for temporary onboarding sessions
        // Data is cleared when browser tab closes, ensuring:
        // - Each onboarding session starts fresh
        // - No stale data from incomplete previous attempts
        // - Better security posture for temporary session data
        storage: createJSONStorage(() => sessionStorage)
      }
    )
  )
);
